import { RequestStatus } from 'features/request/requestSlice';
import type { CatalogUerBaseCostEstimateBasisItem, CatalogUerBaseCostWorkScopeBaseData } from 'gql/graphql';
import { isNotEmpty } from 'utils';

import { useCallback, useMemo, useState } from 'react';

import type { IUseCatalogData } from '../helpers';
import { Tabs, tabsItems } from '../helpers';

import { useCatalogUERBaseCostEstimateBasis } from './useCatalogUERBaseCostEstimateBasis';
import { useCatalogUERBaseCostEstimateBasisResources } from './useCatalogUERBaseCostEstimateBasisResources';
import { useCatalogUERBaseCostWorkScopes } from './useCatalogUERBaseCostWorkScopes';

export const useCatalogUerBaseCostWorkScopesData = ({
    activeTabId,
    catalogUniqueId,
    uerBaseCostId,
}: IUseCatalogData) => {
    const [filterState, setFilterState] = useState<Record<Tabs, Record<string, string[]>>>({
        [Tabs.WORK_SCOPES]: {},
        [Tabs.ESTIMATE_BASIS]: {},
        [Tabs.ESTIMATE_BASIS_RESOURCES]: {},
    });

    const onFiltersUpdated = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] | null }) => {
            setFilterState((prev) => ({
                ...prev,
                [activeTabId]: {
                    ...(prev[activeTabId] || {}),
                    [fieldName]: checkedValues,
                },
            }));
        },
        [activeTabId],
    );

    const {
        items: workScopesItems,
        status: workScopesStatus,
        refresh: workScopesRefresh,
    } = useCatalogUERBaseCostWorkScopes({
        catalogUniqueId,
        uerBaseCostId,
        withEffect: activeTabId === tabsItems[0].id,
    });

    const { items: estimateBasisResourcesItems, status: estimateBasisResourcesStatus } =
        useCatalogUERBaseCostEstimateBasisResources({
            catalogUniqueId,
            uerBaseCostId,
            withEffect: activeTabId === tabsItems[1].id,
        });

    const { items: estimateBasisItems, status: estimateBasisStatus } = useCatalogUERBaseCostEstimateBasis({
        catalogUniqueId,
        uerBaseCostId,
        withEffect: activeTabId === tabsItems[2].id,
    });

    const isLoading = useMemo(() => {
        if (activeTabId === tabsItems[0].id) {
            return workScopesStatus === RequestStatus.FETCHING;
        }

        if (activeTabId === tabsItems[1].id) {
            return estimateBasisResourcesStatus === RequestStatus.FETCHING;
        }

        if (activeTabId === tabsItems[2].id) {
            return estimateBasisStatus === RequestStatus.FETCHING;
        }

        return false;
    }, [activeTabId, estimateBasisResourcesStatus, estimateBasisStatus, workScopesStatus]);

    const currentTabFilterState = useMemo(() => filterState[activeTabId] ?? {}, [activeTabId, filterState]);
    const hasActiveFilter = useMemo(
        () => Object.values(currentTabFilterState).some(isNotEmpty),
        [currentTabFilterState],
    );

    const items = useMemo(() => {
        const applyFilter = (item: CatalogUerBaseCostWorkScopeBaseData | CatalogUerBaseCostEstimateBasisItem) =>
            Object.entries(currentTabFilterState).every(([field, valuesRaw]) => {
                const key = field as keyof typeof item;

                if (valuesRaw.length === 0) {
                    return true;
                }

                if (key in item && item[key] !== undefined) {
                    return valuesRaw.includes(item[key] as string);
                }

                return false;
            });

        if (activeTabId === tabsItems[0].id) {
            return hasActiveFilter ? workScopesItems.filter(applyFilter) : workScopesItems;
        }

        if (activeTabId === tabsItems[1].id) {
            return hasActiveFilter ? estimateBasisResourcesItems.filter(applyFilter) : estimateBasisResourcesItems;
        }

        if (activeTabId === tabsItems[2].id) {
            return hasActiveFilter ? estimateBasisItems.filter(applyFilter) : estimateBasisItems;
        }

        return [];
    }, [
        activeTabId,
        currentTabFilterState,
        estimateBasisItems,
        estimateBasisResourcesItems,
        hasActiveFilter,
        workScopesItems,
    ]);

    const currentFilterData = useMemo(() => {
        if (activeTabId === tabsItems[0].id) {
            return workScopesItems;
        }

        if (activeTabId === tabsItems[1].id) {
            return estimateBasisResourcesItems;
        }

        if (activeTabId === tabsItems[2].id) {
            return estimateBasisItems;
        }

        return [];
    }, [activeTabId, workScopesItems, estimateBasisResourcesItems, estimateBasisItems]);

    return {
        items,
        isLoading,
        workScopesRefresh,
        currentFilterData,
        onFiltersUpdated,
    };
};
